import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, EyeOff, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { BASE_URL } from '../lib/api';
const passwordValidationSchema = Yup.object().shape({
  password: Yup.string()    
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Must include at least one lowercase letter')
    .matches(/[A-Z]/, 'Must include at least one uppercase letter')
    .matches(/[0-9]/, 'Must include at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Must include at least one special character'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
});

export default function SuperAdminResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
 

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Super admin password reset token is missing or invalid.');
      toast({
        title: 'Invalid Reset Link',
        description: 'The password reset link is missing or invalid.',
        variant: 'destructive',
      });
    }
  }, [token, toast]);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: passwordValidationSchema,
    onSubmit: async (values) => {
      if (!token) {
        setError('Reset token is missing. Please request a new password reset.');
        return;
      }

      setError('');
      setMessage('');
      setLoading(true);

      try {
        console.log('SuperAdmin password reset attempt for token:', token);

        const response = await fetch(`${BASE_URL}/authentication/reset-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            token, 
            newPassword: values.password 
          }),
        });

        const data = await response.json();

        if (response.ok) {
          const successMessage = data.message || 'Super admin password has been reset successfully.';
          setMessage(successMessage);
          
          toast({
            title: 'Password Reset Successful',
            description: 'Your super admin password has been updated. You can now log in with your new password.',
          });

          formik.resetForm();
          
          // Redirect to super admin login after a delay
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 2000);

        } else {
          let errorMessage = data.message || 'Failed to reset password. Please try again.';
          
          if (data.message && data.message.includes('expired')) {
            errorMessage = 'Password reset link has expired. Please request a new one.';
          } else if (data.message && data.message.includes('Invalid')) {
            errorMessage = 'Invalid reset token. Please request a new password reset.';
          }
          
          setError(errorMessage);
          
          toast({
            title: 'Password Reset Failed',
            description: errorMessage,
            variant: 'destructive',
          });
        }
        
      } catch (err) {
        console.error('SuperAdmin reset error:', err);
        
        let errorMessage = 'Network error. Please check your connection and try again.';
        
        if (err.name === 'TypeError' && err.message.includes('fetch')) {
          errorMessage = 'Unable to connect to the server. Please try again later.';
        }
        
        setError(errorMessage);
        
        toast({
          title: 'Connection Error',
          description: errorMessage,
          variant: 'destructive',
        });
        
      } finally {
        setLoading(false);
      }
    },
  });

  const handleBackToLogin = () => {
    navigate('/');
  };

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: '' };
    
    let score = 0;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    
    if (score <= 2) return { level: score, text: 'Weak', color: 'text-red-600' };
    if (score <= 4) return { level: score, text: 'Medium', color: 'text-yellow-600' };
    return { level: score, text: 'Strong', color: 'text-green-600' };
  };

  const passwordStrength = getPasswordStrength(formik.values.password);

  return (
    <div className="flex min-h-screen w-full items-center justify-center animated-gradient-background p-4">
        <div className="w-full max-w-sm">
     {/* Super Admin Icon */}
            <div className="mb-8 flex w-full justify-center">
          <img
            src="./evolvv.png"
            alt="evolvv Logo"
            width={200}
            height={50}
            className="dark:invert"
          />
        </div>
      
                          <Card className="w-full shadow-xl bg-card/90 backdrop-blur-sm">
                                <CardHeader className="space-y-1 text-center">
               
                  <CardTitle className="text-2xl text-card-foreground">
                     Reset Super Admin Password
            </CardTitle>
            
            <CardDescription className="text-gray-600">
              Create a strong new password for your administrator account.
            </CardDescription>
          </CardHeader>

          <form onSubmit={formik.handleSubmit}>
            <CardContent className="space-y-6">
              {/* Status Messages */}
              {message && (
                <div className="p-4 rounded-lg text-sm font-medium bg-green-50 text-green-700 border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{message}</span>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="p-4 rounded-lg text-sm font-medium bg-red-50 text-red-700 border border-red-200">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                </div>
              )}

              {/* New Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-medium">
                  New Admin Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new secure password"
                 className="pl-10 bg-background/80 text-foreground placeholder:text-muted-foreground/70"
           value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
                
                {/* Password Strength Indicator */}
                {formik.values.password && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Password Strength:</span>
                      <span className={`text-xs font-medium ${passwordStrength.color}`}>
                        {passwordStrength.text}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          passwordStrength.level <= 2 ? 'bg-red-500' :
                          passwordStrength.level <= 4 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${(passwordStrength.level / 5) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                {formik.touched.password && formik.errors.password && (
                  <p className="text-red-600 text-sm mt-1 font-medium">{formik.errors.password}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your new password"
              className="pl-10 bg-background/80 text-foreground placeholder:text-muted-foreground/70"
          value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <p className="text-red-600 text-sm mt-1 font-medium">{formik.errors.confirmPassword}</p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div className="text-xs text-blue-700">
                  <p className="font-medium mb-2">Password Requirements:</p>
                  <ul className="space-y-1">
                    <li className={`flex items-center gap-2 ${formik.values.password.length >= 12 ? 'text-green-600' : 'text-blue-600'}`}>
                      <span className="w-1 h-1 rounded-full bg-current"></span>
                      At least 12 characters long
                    </li>
                    <li className={`flex items-center gap-2 ${/[a-z]/.test(formik.values.password) ? 'text-green-600' : 'text-blue-600'}`}>
                      <span className="w-1 h-1 rounded-full bg-current"></span>
                      One lowercase letter (a-z)
                    </li>
                    <li className={`flex items-center gap-2 ${/[A-Z]/.test(formik.values.password) ? 'text-green-600' : 'text-blue-600'}`}>
                      <span className="w-1 h-1 rounded-full bg-current"></span>
                      One uppercase letter (A-Z)
                    </li>
                    <li className={`flex items-center gap-2 ${/[0-9]/.test(formik.values.password) ? 'text-green-600' : 'text-blue-600'}`}>
                      <span className="w-1 h-1 rounded-full bg-current"></span>
                      One number (0-9)
                    </li>
                    <li className={`flex items-center gap-2 ${/[!@#$%^&*(),.?":{}|<>]/.test(formik.values.password) ? 'text-green-600' : 'text-blue-600'}`}>
                      <span className="w-1 h-1 rounded-full bg-current"></span>
                      One special character
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-4">
              {/* Reset Button */}
              <Button 
                type="submit" 
             className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 transform hover:scale-105" 
                    disabled={loading || !token || !formik.isValid}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Resetting Password...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Reset Super Admin Password
                  </div>
                )}
              </Button>

              {/* Back to Login */}
              <Button
                type="button"
                variant="ghost"
                 className="w-full text-blue-500 hover:text-blue-600  hover:bg-purple-50 font-medium"
                onClick={handleBackToLogin}
                disabled={loading}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Admin Login
              </Button>

              {/* Support Contact */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Need assistance?{' '}
                  <a 
                    href="mailto:support@revynox.com" 
                       className="font-semibold text-blue-500 hover:text-blue-600  hover:underline transition-colors"
                  >
                    Contact System Administrator
                  </a>
                </p>
              </div>
            </CardFooter>
          </form>
        </Card>

        {/* Bottom Security Notice */}
        <div className="mt-4 text-center">
          <p className="text-xs text-white/60">
            <Shield className="inline h-3 w-3 mr-1" />
            Secure super admin portal protected by enterprise-grade encryption
          </p>
        </div>
      </div>
    </div>
  );
}